import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLiff } from './hooks/useLiff';
import { BottomNav } from './components/BottomNav';
import { HomePage } from './pages/HomePage';
import { BridgeletPage } from './pages/BridgeletPage';
import { BridgeletEditPage } from './pages/BridgeletEditPage';
import { DealsPage } from './pages/DealsPage';
import { LoginPage } from './pages/LoginPage';
import type { Bridgelet, Deal } from './utils/api';
import { setAuthToken, setLineUserId, bridgeletApi, dealsApi, authApi } from './utils/api';

// Mock LIFF ID - replace with actual LIFF ID from LINE Developers Console
const LIFF_ID = import.meta.env.VITE_LIFF_ID || '1234567890-abcdefgh';

function App() {
  const { isReady, isLoggedIn, error, profile, liff } = useLiff(LIFF_ID);
  const [currentPage, setCurrentPage] = useState('home');
  const [isPasswordLoggedIn, setIsPasswordLoggedIn] = useState(false);

  // Real data from API
  const [bridgelet, setBridgelet] = useState<Bridgelet | null>(null);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);

  // Handle password-based login
  const handlePasswordLogin = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      setLoginError(null);

      const response = await authApi.login(email, password);

      // Set auth token for API requests
      setAuthToken(response.accessToken);

      // Mark as password logged in
      setIsPasswordLoggedIn(true);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'เข้าสู่ระบบไม่สำเร็จ';
      setLoginError(message);
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle LINE login
  const handleLineLogin = () => {
    if (liff) {
      liff.login();
    }
  };

  // Fetch data from backend when logged in (either LINE or password)
  useEffect(() => {
    const isAnyLoggedIn = isLoggedIn || isPasswordLoggedIn;

    if (isAnyLoggedIn) {
      const fetchData = async () => {
        try {
          setIsLoading(true);
          setApiError(null);

          // For LINE users, set LINE token and user ID
          if (isLoggedIn && liff && profile) {
            const token = liff.getAccessToken();
            if (token) {
              setAuthToken(token);
            }

            if (profile.userId) {
              setLineUserId(profile.userId);
            }
          }

          // Fetch Bridgelet and Deals data (works for both LINE and password auth)
          const [bridgeletData, dealsData] = await Promise.all([
            bridgeletApi.get().catch(err => {
              console.error('Failed to fetch bridgelet:', err);
              return null;
            }),
            dealsApi.list().catch(err => {
              console.error('Failed to fetch deals:', err);
              return [];
            }),
          ]);

          if (bridgeletData) {
            setBridgelet(bridgeletData);
          }
          setDeals(dealsData);
        } catch (err) {
          console.error('Error fetching data:', err);
          setApiError('ไม่สามารถโหลดข้อมูลได้ กรุณาลองอีกครั้ง');
        } finally {
          setIsLoading(false);
        }
      };

      fetchData();
    }
  }, [isLoggedIn, isPasswordLoggedIn, liff, profile]);

  const handleNavigate = (page: string) => {
    setCurrentPage(page);
  };

  const handleEditBridgelet = () => {
    setCurrentPage('bridgelet-edit');
  };

  const handleSaveBridgelet = (updatedBridgelet: Bridgelet) => {
    setBridgelet(updatedBridgelet);
    setCurrentPage('bridgelet');
  };

  const handleDealClick = (dealId: string) => {
    // TODO: Navigate to deal detail
    console.log('Deal clicked:', dealId);
  };

  if (!isReady) {
    return (
      <div className="min-h-screen bg-mesh-ocean flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 animate-pulse" />
          <p className="text-white/60">กำลังโหลด...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-mesh-ocean flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-2xl p-8 text-center max-w-md"
        >
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-2">เกิดข้อผิดพลาด</h2>
          <p className="text-white/60 mb-4">{error.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold hover:shadow-[0_0_30px_rgba(20,184,166,0.6)] transition-all"
          >
            ลองอีกครั้ง
          </button>
        </motion.div>
      </div>
    );
  }

  if (!isLoggedIn && !isPasswordLoggedIn) {
    return (
      <LoginPage
        onPasswordLogin={handlePasswordLogin}
        onLineLogin={handleLineLogin}
        error={loginError}
        isLoading={isLoading}
      />
    );
  }

  // Show loading state while fetching data
  if (isLoading) {
    return (
      <div className="min-h-screen bg-mesh-ocean flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 animate-pulse" />
          <p className="text-white/60">กำลังโหลดข้อมูล...</p>
        </motion.div>
      </div>
    );
  }

  // Show error state if API failed
  if (apiError) {
    return (
      <div className="min-h-screen bg-mesh-ocean flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-2xl p-8 text-center max-w-md"
        >
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-2">เกิดข้อผิดพลาด</h2>
          <p className="text-white/60 mb-4">{apiError}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold hover:shadow-[0_0_30px_rgba(20,184,166,0.6)] transition-all"
          >
            ลองอีกครั้ง
          </button>
        </motion.div>
      </div>
    );
  }

  const renderPage = () => {
    // Default bridgelet if not loaded yet
    const defaultBridgelet: Bridgelet = {
      id: 'loading',
      name: 'กำลังโหลด...',
      persona: {
        tone: 'friendly',
        negotiationStyle: 'balanced',
        riskTolerance: 'medium',
        language: 'th',
      },
      stats: {
        dealsCompleted: 0,
        successRate: 0,
        avgNegotiationTime: 0,
        totalSaved: 0,
      },
      status: 'active',
    };

    const currentBridgelet = bridgelet || defaultBridgelet;

    switch (currentPage) {
      case 'home':
        return (
          <HomePage
            bridgelet={currentBridgelet}
            recentDeals={deals.slice(0, 3)}
            onNavigate={handleNavigate}
          />
        );
      case 'bridgelet':
        return (
          <BridgeletPage
            bridgelet={currentBridgelet}
            onBack={() => handleNavigate('home')}
            onEdit={handleEditBridgelet}
          />
        );
      case 'bridgelet-edit':
        return (
          <BridgeletEditPage
            bridgelet={currentBridgelet}
            onBack={() => handleNavigate('bridgelet')}
            onSave={handleSaveBridgelet}
          />
        );
      case 'deals':
        return (
          <DealsPage
            deals={deals}
            onBack={() => handleNavigate('home')}
            onDealClick={handleDealClick}
          />
        );
      case 'profile':
        return (
          <div className="glass rounded-2xl p-8 text-center">
            <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden bg-gradient-to-br from-teal-500 to-cyan-500">
              {profile?.pictureUrl ? (
                <img src={profile.pictureUrl} alt={profile.displayName} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-4xl">👤</div>
              )}
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">{profile?.displayName}</h2>
            <p className="text-white/60 mb-6">LINE User ID: {profile?.userId}</p>
            <button
              onClick={() => handleNavigate('home')}
              className="px-6 py-3 rounded-xl glass hover:bg-white/15 transition-all"
            >
              กลับหน้าแรก
            </button>
          </div>
        );
      default:
        return (
          <HomePage
            bridgelet={currentBridgelet}
            recentDeals={deals.slice(0, 3)}
            onNavigate={handleNavigate}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-mesh-ocean">
      <main className="max-w-2xl mx-auto px-4 pt-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {renderPage()}
          </motion.div>
        </AnimatePresence>
      </main>

      <BottomNav currentPage={currentPage} onNavigate={handleNavigate} />
    </div>
  );
}

export default App;
